import { useState } from 'react';
import { Card, Button } from '../components/ui';

const REASONS = [
  'Too expensive',
  'Missing a feature I need',
  'No longer managing rental properties',
  'Switching to another tool',
  'Other',
];

const STEP = { REASON: 'reason', OFFER: 'offer', CONFIRM: 'confirm', DONE: 'done' };

function authHeaders() {
  const ac = localStorage.getItem('lc_access_code');
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    ...(ac ? { 'X-Access-Code': ac } : {}),
  };
}

export default function CancelSubscriptionModal({ isOpen, onClose, onCancelled }) {
  const [step, setStep] = useState(STEP.REASON);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  function reset() {
    setStep(STEP.REASON);
    setReason('');
    setFeedback('');
    setError('');
    setLoading(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleReasonSubmit() {
    if (!reason) {
      setError('Please select a reason to continue.');
      return;
    }
    setError('');
    setStep(reason === 'Too expensive' ? STEP.OFFER : STEP.CONFIRM);
  }

  async function handleAcceptDiscount() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/billing/retention-discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error();
      onCancelled?.();
      handleClose();
    } catch {
      setError('Could not apply the discount — please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmCancel() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/billing/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ reason, feedback }),
      });
      if (!res.ok) throw new Error();
      setStep(STEP.DONE);
      onCancelled?.();
    } catch {
      setError('Could not cancel your subscription — please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(21,34,56,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(440px, 100%)' }}>
        <Card style={{ padding: 28 }}>
          {step === STEP.REASON && (
            <>
              <h3 style={{ fontSize: 20, fontWeight: 300, marginBottom: 6 }}>We're sorry to see you go</h3>
              <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 16 }}>
                Mind telling us why you're cancelling?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8 }}>
                {REASONS.map(r => (
                  <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="cancel-reason"
                      checked={reason === r}
                      onChange={() => setReason(r)}
                    />
                    {r}
                  </label>
                ))}
              </div>
              {error && <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 8 }}>{error}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <Button variant="ghost" onClick={handleClose}>Never mind</Button>
                <Button onClick={handleReasonSubmit}>Continue</Button>
              </div>
            </>
          )}

          {step === STEP.OFFER && (
            <>
              <h3 style={{ fontSize: 20, fontWeight: 300, marginBottom: 6 }}>Would 20% off help?</h3>
              <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>
                We can apply 20% off your next 3 months — no commitment, you can still cancel any time.
              </p>
              {error && <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 8 }}>{error}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <Button variant="ghost" onClick={() => setStep(STEP.CONFIRM)} disabled={loading}>
                  No thanks, cancel anyway
                </Button>
                <Button onClick={handleAcceptDiscount} loading={loading}>
                  Apply discount and stay
                </Button>
              </div>
            </>
          )}

          {step === STEP.CONFIRM && (
            <>
              <h3 style={{ fontSize: 20, fontWeight: 300, marginBottom: 6 }}>Confirm cancellation</h3>
              <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>
                You'll keep full access until the end of your current billing period. No further charges after that.
              </p>
              <textarea
                placeholder="Anything else you'd like us to know? (optional)"
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                rows={3}
                style={{
                  width: '100%', marginTop: 14, padding: 10, borderRadius: 8,
                  border: '1px solid var(--border)', fontFamily: 'inherit', fontSize: 14, resize: 'vertical',
                }}
              />
              {error && <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 8 }}>{error}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <Button variant="ghost" onClick={handleClose} disabled={loading}>Keep my subscription</Button>
                <Button onClick={handleConfirmCancel} loading={loading} style={{ background: 'var(--danger)' }}>
                  Confirm cancellation
                </Button>
              </div>
            </>
          )}

          {step === STEP.DONE && (
            <>
              <h3 style={{ fontSize: 20, fontWeight: 300, marginBottom: 6 }}>Cancellation confirmed</h3>
              <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>
                Your subscription won't renew. You'll keep access to LandlordComply until the end of your current period.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}